import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { NavItem } from './navItems';

interface ExpandedState {
    root: Record<string, boolean>;
    groups: Record<string, string | null>;
}

/** Walk the NavItem tree and return the ancestor chain from root to the matching leaf */
function findAncestorChain(pathname: string, items: NavItem[], chain: NavItem[] = []): NavItem[] | null {
    for (const item of items) {
        const currentChain = [...chain, item];
        if (!item.content && item.path === pathname) return currentChain;
        if (item.content) {
            const result = findAncestorChain(pathname, item.content, currentChain);
            if (result) return result;
        }
    }
    return null;
}

/** Build the expanded state that shows the path to a given pathname */
function buildExpandedStateForPath(pathname: string, navItems: NavItem[]): ExpandedState {
    const root: Record<string, boolean> = {};
    const groups: Record<string, string | null> = {};

    const chain = findAncestorChain(pathname, navItems);
    if (!chain || chain.length === 0) return { root, groups };

    // First item is root level
    root[chain[0].path] = true;

    // Each parent→child pair: groups[parent.path] = child.path
    for (let i = 0; i < chain.length - 1; i++) {
        if (chain[i].content) {
            groups[chain[i].path] = chain[i + 1].path;
        }
    }

    return { root, groups };
}

export default function useExpandedState(navItems: NavItem[]) {
    const location = useLocation();

    const [state, setState] = useState<ExpandedState>(() =>
        buildExpandedStateForPath(location.pathname, navItems)
    );

    // Auto-expand on route change
    useEffect(() => {
        setState((prev) => {
            const pathState = buildExpandedStateForPath(location.pathname, navItems);
            // Merge: keep previously opened root sections, add the new ones
            return {
                root: { ...prev.root, ...pathState.root },
                groups: { ...prev.groups, ...pathState.groups }
            };
        });
    }, [location.pathname]);

    const toggle = useCallback((itemPath: string, parentPath: string | null) => {
        setState((prev) => {
            if (parentPath === null) {
                // Root level: toggle independently
                return {
                    ...prev,
                    root: { ...prev.root, [itemPath]: !prev.root[itemPath] }
                };
            }
            // Non-root: accordion — toggle or replace
            const currentOpen = prev.groups[parentPath];
            const newGroups = { ...prev.groups };

            if (currentOpen === itemPath) {
                // Closing: remove this and any descendant groups
                newGroups[parentPath] = null;
                // Clean up descendant groups
                for (const key of Object.keys(newGroups)) {
                    if (key.startsWith(itemPath)) {
                        delete newGroups[key];
                    }
                }
            } else {
                // Opening: set this, clean up previous sibling's descendants
                if (currentOpen) {
                    for (const key of Object.keys(newGroups)) {
                        if (key.startsWith(currentOpen)) {
                            delete newGroups[key];
                        }
                    }
                }
                newGroups[parentPath] = itemPath;
            }

            return { ...prev, groups: newGroups };
        });
    }, []);

    /** Open a top-level section without closing it if it is already open —
     *  what the collapsed rail's icons do, where a toggle would let a click
     *  expand the sidebar onto a section it just shut. */
    const expandRoot = useCallback((itemPath: string) => {
        setState((prev) => ({ ...prev, root: { ...prev.root, [itemPath]: true } }));
    }, []);

    const isExpanded = useCallback(
        (itemPath: string, parentPath: string | null): boolean => {
            if (parentPath === null) return !!state.root[itemPath];
            return state.groups[parentPath] === itemPath;
        },
        [state]
    );

    return { isExpanded, toggle, expandRoot };
}
